/**
 * Meetings page (§3, screenshots/07–09). "Following · N" lists only meetings that touch what you
 * follow, each with the lines saying why; "All meetings" lists every meeting. Paints from the
 * Following snapshot (snapshot:following) and the follows inlined with snapshot:me.
 */
import { useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CalendarSync, Check, ChevronsUpDown, Plus, Search, SlidersHorizontal, Users } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { selectNewCount, useFollowing } from "../stores/followingStore";
import { useFollows } from "../stores/followsStore";
import { toastStore } from "../stores/toastStore";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import { Face, FaceStack } from "../components/shared/Face";
import { MeetingRow } from "../components/following/MeetingRow";
import { List, ListRow, RowLead, RowMain, RowTrail } from "../components/shared/List";
import { TypeBadge } from "../components/following/TypeBadge";
import { FollowingSide } from "../components/following/FollowingSide";
import { LiveBanner } from "../components/following/LiveBanner";
import { FollowSelection } from "../components/shared/FollowSelection";
import { persistSeen } from "../stores/seenSync";
import { startLiveNow } from "../realtime/liveNow";
import { AllCaughtUp, NoResults } from "../components/shared/States";
import { downloadMeetingIcs } from "../lib/ics";
import { duration, easing, prefersReducedMotion, recipes, springEasing, reducedFallback } from "../motion";
import type { CatchUpLine, Follow, FollowType, Match, MeetingSummary, Person } from "../types";
import { onTablistKey } from "../hooks/useTabs";
import { useSlashToFocus } from "../hooks/useShortcuts";
import { AllMeetings } from "../meetings/AllMeetings";

const CHIPS_SHOWN = 4;
const TYPE_ORDER: FollowType[] = ["legislation", "person", "committee", "topic"];
const TYPE_LABEL: Record<FollowType, string> = { legislation: "Legislation", person: "People", topic: "Topics", committee: "Committees" };
const ymd = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
const isUpcoming = (m: MeetingSummary) => m.status === "upcoming" || new Date(m.startsAt).getTime() > Date.now();
/** "City Council, Wed Oct 14 at 10:00 AM" (screenshots/54 state 5). */
const nextLabel = (m: MeetingSummary) => {
  const d = new Date(m.startsAt), tz = { timeZone: "America/Chicago" } as const;
  return `${m.body}, ${d.toLocaleDateString("en-US", { ...tz, weekday: "short", month: "short", day: "numeric" }).replace(",", "")} at ${d.toLocaleTimeString("en-US", { ...tz, hour: "numeric", minute: "2-digit" })}`;
};

/** Ask the command palette (step 7) to add a follow. */
const openAddFollow = (type?: FollowType) => dispatchEvent(new CustomEvent("palette:open", { detail: { mode: "follow", type } }));

/** Follow added (motion 5): a new chip pops in with the spring, then its teal ring fades over 1.2 s. */
function popIn(el: HTMLElement | null) {
  if (!el || el.dataset.popped) return;
  el.dataset.popped = "1";
  if (prefersReducedMotion()) { el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: reducedFallback.duration }); return; }
  const spring = springEasing();
  el.animate([{ opacity: 0, transform: `scale(${recipes.followAdded.from.scale})` }, { opacity: 1, transform: "scale(1)" }], { duration: spring.duration, easing: spring.easing });
}

function FollowChip({ f, person, isNew }: { f: Follow; person?: Person; isNew?: boolean }) {
  return (
    <span className="follow-chip" data-new={isNew || undefined} ref={isNew ? popIn : undefined}>
      {f.type === "person" ? <Face name={person?.name ?? f.label} photoUrl={person?.photoUrl} size={16} /> : <TypeBadge type={f.type} size={16} />}
      <span className="follow-chip__text">{f.label}</span>
      {f.scope === "team" && <span className="team-mark"><Users size={10} />Team</span>}
    </span>
  );
}

function FollowingBar({ follows, people, canManageTeam }: { follows: Follow[]; people: Record<string, Person>; canManageTeam: boolean }) {
  const [all, setAll] = useState(false);
  const initial = useRef<Set<string> | null>(null);
  initial.current ??= new Set(follows.map((f) => f.id));
  const added = (f: Follow) => !initial.current!.has(f.id);
  // Chips added on this visit always show (they pop in at the end of the bar).
  const shown = all ? follows : [...follows.filter((f) => !added(f)).slice(0, CHIPS_SHOWN), ...follows.filter(added)];
  const hidden = follows.length - shown.length;
  return (
    <div className="following-bar">
      <span className="following-bar__label">Following</span>
      {shown.map((f) => <FollowChip key={f.id} f={f} person={f.refId ? people[f.refId] : undefined} isNew={added(f)} />)}
      {!all && hidden > 0 && <button className="follow-more" onClick={() => setAll(true)}>+{hidden}</button>}
      <button className="follow-add" aria-label="Follow something" onClick={() => openAddFollow()}><Plus size={12} /></button>
      {canManageTeam && follows.some((f) => f.scope === "team") && (
        <a className="manage-team" href="/dashboard/settings#team-follows"><SlidersHorizontal size={12} />Manage team follows</a>
      )}
    </div>
  );
}

function BriefLine({ line, people }: { line: CatchUpLine; people: Record<string, Person> }) {
  const faces = (line.peopleIds ?? []).map((id) => people[id]).filter(Boolean);
  const lead = line.type === "person" && faces.length > 0;
  return (
    <ListRow className="brief-line">
      <RowLead>{lead ? <FaceStack people={faces} size={18} /> : <TypeBadge type={line.type} />}</RowLead>
      <RowMain min={120}><span className="brief-line__text" data-follow-source>{line.text}</span></RowMain>
      {!lead && faces.length > 0 && <RowTrail><FaceStack people={faces} size={18} /></RowTrail>}
    </ListRow>
  );
}

/** "Since your last visit" (§3.1); once every new match is seen it folds into one line (spec 11 card 5). */
function CatchUp({ caughtUp }: { caughtUp: boolean }) {
  const { lines, range, people } = useFollowing(useShallow((s) => ({ lines: s.catchUp, range: s.catchUpRange, people: s.people })));
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const lastHeight = useRef<number | null>(null);
  const folded = caughtUp && !open;
  // Height change (300 ms, standard): animate from the previous height to the new one.
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const to = el.offsetHeight, from = lastHeight.current;
    lastHeight.current = to;
    if (from == null || from === to) return;
    if (prefersReducedMotion()) el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: reducedFallback.duration });
    else el.animate([{ height: `${from}px`, overflow: "hidden" }, { height: `${to}px`, overflow: "hidden" }], { duration: duration.slow, easing: easing.standard });
  }, [folded]);
  if (!lines.length) return null;
  const since = range.split(/\s[–-]\s/).pop();
  return (
    <div ref={wrap}>
      {folded ? (
        <div className="catch-up catch-up--done">
          <span className="catch-up__check"><Check size={12} /></span>
          <span className="catch-up__done-text">
            <span className="catch-up__done-title">You're all caught up</span>
            {since && <span className="catch-up__done-sub">Nothing new on your follows since {since}</span>}
          </span>
          <button className="catch-up__show" onClick={() => setOpen(true)}>Show summary</button>
        </div>
      ) : (
        <section className="catch-up" aria-label="Since your last visit">
          <div className="catch-up__head"><h2>Since your last visit</h2>{range && <span>{range}</span>}</div>
          <List variant="plain" className="brief-lines">{lines.slice(0, 3).map((l, i) => <BriefLine key={i} line={l} people={people} />)}</List>
        </section>
      )}
    </div>
  );
}

/** "N new" (motion 4): counts down as matches are seen, then fades out instead of vanishing. */
function NewBadge({ count }: { count: number }) {
  const last = useRef(count);
  if (count > 0) last.current = count;
  const [gone, setGone] = useState(count === 0);
  useEffect(() => {
    if (count > 0) { setGone(false); return; }
    const t = setTimeout(() => setGone(true), prefersReducedMotion() ? reducedFallback.duration : recipes.newToSeen.duration);
    return () => clearTimeout(t);
  }, [count]);
  if (gone && count === 0) return null;
  return <span className="new-badge" data-seen={count === 0 || undefined} aria-live="polite">{last.current} new</span>;
}

export function MeetingsPage({ canManageTeam }: { canManageTeam: boolean }) {
  const follows = useFollows((s) => s.follows);
  const { meetings, matchIdsByMeeting, matchesById, people, othersLabel, newCount } = useFollowing(useShallow((s) => ({
    meetings: s.meetings, matchIdsByMeeting: s.matchIdsByMeeting, matchesById: s.matchesById, people: s.people,
    othersLabel: s.othersLabel, newCount: selectNewCount(s),
  })));
  const liveId = useFollowing((s) => s.live?.meetingId ?? null);
  // Live now: follow the live meeting's speaker turns and status (opened after paint).
  useEffect(() => (liveId ? startLiveNow(liveId) : undefined), [liveId]);
  const [view, setView] = useState<"following" | "all">(() => (new URLSearchParams(location.search).get("view") === "all" ? "all" : "following"));
  const [type, setType] = useState<FollowType | "all">("all");
  const [query, setQuery] = useState("");
  const q = useDeferredValue(query.trim().toLowerCase());
  const [showOthers, setShowOthers] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashToFocus(searchRef);   // in-page search shows "/" (§2.17 one meaning per shortcut; ⌘K is global search)

  const counts = useMemo(() => {
    const c: Partial<Record<FollowType, number>> = {};
    for (const f of follows) c[f.type] = (c[f.type] ?? 0) + 1;
    return c;
  }, [follows]);

  const filter = useMemo(() => (m: Match) =>
    (type === "all" || m.type === type) && (!q || `${m.label} ${m.detail}`.toLowerCase().includes(q)), [type, q]);
  const matched = (m: MeetingSummary) => (matchIdsByMeeting[m.id] ?? []).some((id) => filter(matchesById[id]) || (!!q && m.body.toLowerCase().includes(q)));
  const hasMatches = (m: MeetingSummary) => (matchIdsByMeeting[m.id] ?? []).length > 0;

  const visible = meetings.filter((m) => m.id === liveId || (view === "all" ? !q || m.body.toLowerCase().includes(q) || matched(m) : matched(m) || (showOthers && !hasMatches(m) && !q && type === "all")));
  // A live meeting leads the upcoming section (it's happening now, not past).
  const isNext = (m: MeetingSummary) => m.id === liveId || isUpcoming(m);
  const upcoming = visible.filter(isNext).sort((a, b) => (a.id === liveId ? -1 : b.id === liveId ? 1 : a.startsAt.localeCompare(b.startsAt)));
  const past = visible.filter((m) => !isNext(m)).sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  const others = meetings.filter((m) => !hasMatches(m)).length;
  const catchUpRange = useFollowing((s) => s.catchUpRange);
  const nextMeeting = meetings.filter((m) => isUpcoming(m)).sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
  const followedUpcoming = useMemo(() => new Set(meetings.filter((m) => isUpcoming(m) && hasMatches(m)).map((m) => ymd(m.startsAt))), [meetings, matchIdsByMeeting]); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    const res = await fetch("/api/dashboard/calendar", { method: "POST", credentials: "include" }).catch(() => null);
    const url = res?.ok ? ((await res.json()) as { url?: string }).url : null;
    if (url) location.href = url.replace(/^https?:/, "webcal:");
    else toastStore.getState().show({ message: "Couldn't create your calendar feed. Try again." });
  };
  const pickDay = (day: string) => {
    const m = meetings.find((x) => ymd(x.startsAt) === day);
    if (!m) return;
    if (!hasMatches(m)) setShowOthers(true);
    requestAnimationFrame(() => document.getElementById(`meeting-${m.id}`)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" }));
  };

  const section = (label: string, rows: MeetingSummary[], up: boolean) => rows.length > 0 && (
    <div className="meeting-section">
      <h3 className="section-label">{label}</h3>
      <List className="meeting-section__list">
        {rows.map((m) => <MeetingRow key={m.id} meeting={m} upcoming={up} filter={filter} onSeen={persistSeen} />)}
      </List>
    </div>
  );

  return (
    <div className="meetings-page">
      <header className="page-header">
        <div className="page-header__title">
          <div className="title-row">
            <h1 className="page-title">Meetings</h1>
            <div className="view-switch" role="tablist" aria-label="View" onKeyDown={onTablistKey}>
              <button role="tab" aria-selected={view === "following"} onClick={() => setView("following")}>
                Following · {follows.length}
                <NewBadge count={newCount} />
              </button>
              <button role="tab" aria-selected={view === "all"} onClick={() => setView("all")}>All meetings{view === "all" ? ` · ${meetings.length}` : ""}</button>
            </div>
            <ConnectionStatus />
          </div>
        </div>
        <button className="btn" onClick={() => void sync()}><CalendarSync size={14} />Sync to calendar</button>
      </header>

      {view === "all" ? <AllMeetings /> : (<>
      <FollowingBar follows={follows} people={people} canManageTeam={canManageTeam} />
        <CatchUp caughtUp={newCount === 0} />
  
        <div className="filter-bar">
          <label className="follow-search">
            <Search size={15} />
            <input ref={searchRef} placeholder="Search within your follows" aria-keyshortcuts="/" aria-label="Search within your follows" value={query} onChange={(e) => setQuery(e.target.value)} />
            <kbd>/</kbd>
          </label>
          <div className="type-filter" role="tablist" aria-label="Filter by type" onKeyDown={onTablistKey}>
            <button role="tab" aria-selected={type === "all"} onClick={() => setType("all")}>All follows · {follows.length}</button>
            {TYPE_ORDER.filter((t) => counts[t]).sort((a, b) => counts[b]! - counts[a]!).map((t) => (
              <button key={t} role="tab" aria-selected={type === t} onClick={() => setType(t)}>
                <TypeBadge type={t} size={16} />{TYPE_LABEL[t]} · {counts[t]}
              </button>
            ))}
          </div>
        </div>
  
        <div className="following-body">
          <div className="meeting-list">
            <LiveBanner />
            <div className="matched-meetings">
              {section("Upcoming", upcoming, true)}
              {section("Past", past, false)}
            </div>
            {/* screenshots/54 states 5–6: never an empty list */}
            {!upcoming.length && !past.length && !!q && (
              <NoResults query={query.trim()} onBroaden={() => dispatchEvent(new CustomEvent("palette:open", { detail: { mode: "search", q: query.trim(), scope: "all" } }))} />
            )}
            {!upcoming.length && !past.length && !q && type === "all" && view === "following" && follows.length > 0 && (
              <AllCaughtUp items={follows.length} since={catchUpRange.split(/\s[–-]\s/).pop() || ""} next={nextMeeting ? nextLabel(nextMeeting) : null}
                onAddToCalendar={nextMeeting ? () => downloadMeetingIcs(nextMeeting) : undefined} />
            )}
            {view === "following" && !showOthers && others > 0 && !q && type === "all" && (
              <button className="other-meetings" onClick={() => setShowOthers(true)}>
                <ChevronsUpDown size={14} />
                <span>{othersLabel ?? `${others} other meetings didn't touch your follows`}</span>
                <span className="other-meetings__show">Show</span>
              </button>
            )}
          </div>
          <FollowingSide followedUpcoming={followedUpcoming} onPickDay={pickDay} onTrack={() => openAddFollow("legislation")} />
        </div>
      </>)}
      <FollowSelection />
    </div>
  );
}
